// Hand-curated extra aliases beyond what the Natural Earth dataset provides.
// Keyed by ISO 3166-1 alpha-3. Each entry is a list of additional spellings
// users may plausibly type. The fuzzy matcher already handles typos within
// the configured Levenshtein tolerance, so this list focuses on:
//   - Short / informal English names ("Bahamas" vs "The Bahamas",
//     "St Lucia" vs "Saint Lucia", "PNG" vs "Papua New Guinea").
//   - Cross-language alternatives — the dataset's ADMIN field is sometimes
//     a native spelling that English speakers won't think to type
//     ("Cabo Verde" → "Cape Verde", "Sint Maarten" → "Saint Martin",
//     "Türkiye" → "Turkey", "eSwatini" → "Swaziland").
//   - Anything otherwise too far apart in edit distance to be reached by
//     the default Levenshtein tolerance.
//
// The name-variants module strips leading qualifiers ("Republic of X" → "X")
// and splits compound names on " and ", so this list doesn't need to repeat
// those derivations.

export const COUNTRY_ALIASES: Record<string, string[]> = {
  // === Americas (North / Central / Caribbean) ===
  USA: ["USA", "US", "U.S.", "U.S.A.", "America", "United States"],
  CAN: ["Canada"],
  MEX: ["Mexico"],
  GTM: ["Guatemala"],
  BLZ: ["Belize"],
  SLV: ["El Salvador"],
  HND: ["Honduras"],
  NIC: ["Nicaragua"],
  CRI: ["Costa Rica"],
  PAN: ["Panama"],
  CUB: ["Cuba"],
  JAM: ["Jamaica"],
  HTI: ["Haiti"],
  DOM: ["Dominican Republic", "DR"],
  BHS: ["Bahamas"],
  BRB: ["Barbados"],
  TTO: ["Trinidad", "Tobago"],
  GRD: ["Grenada"],
  LCA: ["Saint Lucia", "St Lucia", "St. Lucia"],
  VCT: ["Saint Vincent", "St Vincent", "St. Vincent", "St Vincent and the Grenadines"],
  KNA: ["Saint Kitts", "St Kitts", "St. Kitts", "St Kitts and Nevis", "Kitts and Nevis"],
  ATG: ["Antigua"],
  DMA: ["Dominica"],
  ABW: ["Aruba"],
  CUW: ["Curacao", "Curaçao"],
  SXM: [
    "Sint Maarten",
    "Saint Martin",
    "St Martin",
    "St. Martin",
    "Saint Maarten",
  ],
  GRL: ["Greenland", "Kalaallit Nunaat"],

  // === Americas (South) ===
  BRA: ["Brazil", "Brasil"],
  ARG: ["Argentina"],
  CHL: ["Chile"],
  COL: ["Colombia"],
  PER: ["Peru"],
  VEN: ["Venezuela"],
  ECU: ["Ecuador"],
  BOL: ["Bolivia"],
  PRY: ["Paraguay"],
  URY: ["Uruguay"],
  GUY: ["Guyana"],
  SUR: ["Suriname", "Surinam"],
  FLK: ["Falklands", "Falkland Islands", "Malvinas", "Islas Malvinas"],

  // === Europe ===
  GBR: [
    "UK",
    "U.K.",
    "Britain",
    "Great Britain",
    "England",
    "United Kingdom",
  ],
  IRL: ["Ireland", "Eire", "Éire"],
  FRA: ["France"],
  DEU: ["Germany", "Deutschland"],
  ITA: ["Italy", "Italia"],
  ESP: ["Spain", "España"],
  PRT: ["Portugal"],
  NLD: ["Holland", "Netherlands", "The Netherlands", "Nederland"],
  BEL: ["Belgium", "Belgique", "België"],
  CHE: ["Switzerland", "Swiss", "Schweiz", "Suisse"],
  AUT: ["Austria", "Österreich", "Osterreich"],
  POL: ["Poland", "Polska"],
  CZE: ["Czechia", "Czech Republic", "Czech"],
  SVK: ["Slovakia"],
  HUN: ["Hungary", "Magyarország"],
  ROU: ["Romania", "Rumania", "Roumania"],
  BGR: ["Bulgaria"],
  GRC: ["Greece", "Hellas", "Ellada"],
  MKD: ["Macedonia", "North Macedonia", "FYROM"],
  ALB: ["Albania", "Shqipëria"],
  SRB: ["Serbia"],
  MNE: ["Montenegro", "Crna Gora"],
  BIH: ["Bosnia", "Bosnia & Herzegovina", "Herzegovina"],
  HRV: ["Croatia", "Hrvatska"],
  SVN: ["Slovenia", "Slovenija"],
  KOS: ["Kosovo", "Kosova"],
  XKX: ["Kosovo", "Kosova"],
  RKS: ["Kosovo", "Kosova"],
  DNK: ["Denmark", "Danmark"],
  SWE: ["Sweden", "Sverige"],
  NOR: ["Norway", "Norge"],
  FIN: ["Finland", "Suomi"],
  ISL: ["Iceland", "Ísland", "Island"],
  EST: ["Estonia", "Eesti"],
  LVA: ["Latvia", "Latvija"],
  LTU: ["Lithuania", "Lietuva"],
  BLR: ["Belarus", "Byelorussia", "White Russia"],
  UKR: ["Ukraine"],
  MDA: ["Moldova"],
  RUS: ["Russia", "Russian Federation"],
  AND: ["Andorra"],
  MCO: ["Monaco"],
  LUX: ["Luxembourg", "Lëtzebuerg"],
  LIE: ["Liechtenstein"],
  SMR: ["San Marino"],
  MLT: ["Malta"],
  VAT: ["Vatican", "Vatican City", "Holy See"],
  GIB: ["Gibraltar"],
  IMN: ["Isle of Man", "Mann"],
  JEY: ["Jersey"],
  GGY: ["Guernsey"],
  ALA: ["Aland", "Åland", "Aland Islands", "Åland Islands"],

  // === North Africa & Middle East ===
  EGY: ["Egypt", "Misr"],
  LBY: ["Libya"],
  TUN: ["Tunisia"],
  DZA: ["Algeria"],
  MAR: ["Morocco", "Maroc", "Maghreb"],
  SDN: ["Sudan"],
  SAU: ["Saudi Arabia", "KSA"],
  YEM: ["Yemen"],
  OMN: ["Oman"],
  ARE: ["UAE", "U.A.E.", "Emirates", "United Arab Emirates"],
  QAT: ["Qatar"],
  BHR: ["Bahrain"],
  KWT: ["Kuwait"],
  IRQ: ["Iraq"],
  JOR: ["Jordan"],
  SYR: ["Syria"],
  LBN: ["Lebanon"],
  ISR: ["Israel"],
  TUR: ["Turkey", "Türkiye", "Turkiye"],
  CYP: ["Cyprus"],
  CYN: ["Northern Cyprus", "North Cyprus", "TRNC"],
  ARM: ["Armenia", "Hayastan"],
  AZE: ["Azerbaijan"],
  GEO: ["Georgia", "Sakartvelo"],

  // === Sub-Saharan Africa ===
  NGA: ["Nigeria"],
  GHA: ["Ghana"],
  CIV: ["Ivory Coast", "Cote d'Ivoire", "Côte d'Ivoire"],
  SEN: ["Senegal"],
  GMB: ["Gambia"],
  GIN: ["Guinea"],
  GNB: ["Guinea-Bissau", "Guinea Bissau"],
  SLE: ["Sierra Leone"],
  LBR: ["Liberia"],
  TGO: ["Togo"],
  BEN: ["Benin"],
  BFA: ["Burkina Faso", "Upper Volta"],
  MLI: ["Mali"],
  NER: ["Niger"],
  MRT: ["Mauritania"],
  CPV: ["Cape Verde", "Cabo Verde"],
  CMR: ["Cameroon", "Cameroun"],
  CAF: ["Central African Republic", "CAR"],
  TCD: ["Chad", "Tchad"],
  GAB: ["Gabon"],
  COG: ["Congo", "Congo-Brazzaville", "Republic of Congo", "Republic of the Congo"],
  COD: [
    "DRC",
    "DR Congo",
    "Congo-Kinshasa",
    "Democratic Republic of Congo",
    "Democratic Republic of the Congo",
    "Zaire",
  ],
  GNQ: ["Equatorial Guinea"],
  STP: [
    "Sao Tome",
    "Sao Tome and Principe",
    "São Tomé",
    "São Tomé and Príncipe",
  ],
  AGO: ["Angola"],
  NAM: ["Namibia"],
  BWA: ["Botswana"],
  ZAF: ["South Africa", "RSA"],
  LSO: ["Lesotho"],
  SWZ: ["Eswatini", "eSwatini", "Swaziland"],
  ZWE: ["Zimbabwe", "Rhodesia"],
  ZMB: ["Zambia"],
  MOZ: ["Mozambique"],
  MWI: ["Malawi"],
  MDG: ["Madagascar"],
  MUS: ["Mauritius"],
  SYC: ["Seychelles"],
  COM: ["Comoros"],
  TZA: ["Tanzania"],
  KEN: ["Kenya"],
  UGA: ["Uganda"],
  RWA: ["Rwanda"],
  BDI: ["Burundi"],
  ETH: ["Ethiopia", "Abyssinia"],
  ERI: ["Eritrea"],
  DJI: ["Djibouti"],
  SOM: ["Somalia"],
  SOL: ["Somaliland"],
  SSD: ["South Sudan"],
  IOT: ["BIOT", "Chagos Islands", "British Indian Ocean Territory"],

  // === Central Asia ===
  KAZ: ["Kazakhstan"],
  UZB: ["Uzbekistan"],
  TKM: ["Turkmenistan"],
  TJK: ["Tajikistan", "Tadjikistan"],
  KGZ: ["Kyrgyzstan", "Kirghizia", "Kyrgyz Republic"],

  // === East Asia ===
  CHN: ["China", "PRC"],
  JPN: ["Japan", "Nippon", "Nihon"],
  KOR: ["South Korea", "Korea", "Republic of Korea", "ROK"],
  PRK: ["North Korea", "DPRK", "Democratic People's Republic of Korea"],
  MNG: ["Mongolia"],
  TWN: ["Taiwan", "Republic of China", "ROC", "Chinese Taipei"],
  HKG: ["Hong Kong", "HK", "Hong Kong SAR"],
  MAC: ["Macau", "Macao", "Macao SAR"],

  // === South & Southeast Asia ===
  IND: ["India", "Bharat"],
  PAK: ["Pakistan"],
  BGD: ["Bangladesh"],
  NPL: ["Nepal"],
  BTN: ["Bhutan"],
  LKA: ["Sri Lanka", "Ceylon"],
  MDV: ["Maldives"],
  AFG: ["Afghanistan"],
  IRN: ["Iran", "Persia"],
  MMR: ["Myanmar", "Burma"],
  THA: ["Thailand", "Siam"],
  LAO: ["Laos", "Lao", "Lao PDR"],
  VNM: ["Vietnam", "Viet Nam"],
  KHM: ["Cambodia", "Kampuchea"],
  MYS: ["Malaysia"],
  SGP: ["Singapore"],
  BRN: ["Brunei", "Brunei Darussalam"],
  IDN: ["Indonesia"],
  PHL: ["Philippines", "The Philippines"],
  TLS: ["East Timor", "Timor-Leste", "Timor Leste"],

  // === Oceania ===
  AUS: ["Australia", "Oz"],
  NZL: ["New Zealand", "NZ", "Aotearoa"],
  PNG: ["Papua New Guinea", "PNG"],
  FJI: ["Fiji"],
  SLB: ["Solomon Islands", "Solomons"],
  VUT: ["Vanuatu", "New Hebrides"],
  WSM: ["Samoa", "Western Samoa"],
  TON: ["Tonga"],
  TUV: ["Tuvalu", "Ellice Islands"],
  KIR: ["Kiribati", "Gilbert Islands"],
  NRU: ["Nauru"],
  PLW: ["Palau"],
  MHL: ["Marshall Islands"],
  FSM: ["Micronesia", "Federated States of Micronesia", "FSM"],
};
